import { definedMap } from "@glideapps/ts-necessities";
import AppIcon from "common/app-icon";
import { asString, isLoadedDefined, loadedDefinedMap } from "common/components/component-helpers";
import { getLocalizedString } from "common/components/localization";
import { AppEnvironment, ForceValueProducer, RenderEnvironment, ValueProducer } from "common/components/types";
import { SubscribingReactComponent } from "common/components/uis";
import { DeviceFormFactor } from "common/render/form-factor";
import { defined, glideViralReferralLink, undefinedIfEmptyOrWhitespaceString } from "common/Support";
import * as React from "react";
import { createPortal } from "react-dom";
// @ts-ignore 1706
import Swipe from "react-easy-swipe";
import ReactResizeDetector from "react-resize-detector";
import { TabWithVisibility } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import { pushShareScreen } from "webapp/lib/share-utils";

import { AppCredentials } from "../../../chrome/smart/app-container/app-container-types";
import { ModalController, withModals } from "../../../chrome/smart/app-modal-provider/app-modal-provider";
import { AuthScreenMode } from "../../../chrome/smart/app-modal-provider/app-modal-provider-types";
import { leftPanelExpandBreakpoint } from "../../../chrome/smart/tablet-container/tablet-container-style";
import { FLYOUT_ROOT } from "../../../lib/constants";
import { withCredentials } from "../../../lib/with-credentials";
import {
    FlyoutContainer,
    FlyoutMenuWrapper,
    Footer,
    Header,
    HeaderEmail,
    HeaderImg,
    HeaderName,
    MenuArea,
    MenuItemWrapper,
    Scroller,
} from "./flyout-style";

interface Props {
    isOpen: boolean;
    items: readonly TabWithVisibility[];
    selected: TabWithVisibility;
    onSelected: (tab: TabWithVisibility) => void;
    onClose: () => void;
    appEnvironment: AppEnvironment;
    credentials: AppCredentials;
    modals: ModalController;
    userProfile: ValueProducer | undefined;
    renderEnvironment: RenderEnvironment;
}
interface State {}

const NoUserImg = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M24 0C10.752 0 0 10.752 0 24C0 37.248 10.752 48 24 48C37.248 48 48 37.248 48 24C48 10.752 37.248 0 24 0ZM12.168 39.072C13.2 36.912 19.488 34.8 24 34.8C28.512 34.8 34.824 36.912 35.832 39.072C32.568 41.664 28.464 43.2 24 43.2C19.536 43.2 15.432 41.664 12.168 39.072ZM39.264 35.592C35.832 31.416 27.504 30 24 30C20.496 30 12.168 31.416 8.736 35.592C6.288 32.376 4.8 28.368 4.8 24C4.8 13.416 13.416 4.8 24 4.8C34.584 4.8 43.2 13.416 43.2 24C43.2 28.368 41.712 32.376 39.264 35.592ZM24 9.6C19.344 9.6 15.6 13.344 15.6 18C15.6 22.656 19.344 26.4 24 26.4C28.656 26.4 32.4 22.656 32.4 18C32.4 13.344 28.656 9.6 24 9.6ZM24 21.6C22.008 21.6 20.4 19.992 20.4 18C20.4 16.008 22.008 14.4 24 14.4C25.992 14.4 27.6 16.008 27.6 18C27.6 19.992 25.992 21.6 24 21.6Z"
            fill="currentColor"
        />
    </svg>
);

const MadeWithGlide = () => (
    <svg width="124" height="20" viewBox="0 0 124 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M26.9767 15.0527H28.6407V9.24173L31.0847 15.0527H32.4107L34.8547 9.24173V15.0527H36.5187V5.95273H34.5297L31.7477 12.5437L28.9657 5.95273H26.9767V15.0527ZM40.4994 8.39673C39.1084 8.39673 37.9514 9.22873 37.7434 10.5287L39.1994 10.7887C39.2774 10.0867 39.7844 9.64473 40.4994 9.64473C41.2924 9.64473 41.7994 10.0997 41.7994 10.8407V10.9057L40.5774 11.0227C38.6274 11.2307 37.6524 11.9197 37.6524 13.2067C37.6524 14.3247 38.5494 15.1567 39.8364 15.1567C40.6424 15.1567 41.4354 14.7407 41.7994 14.1037V15.0527H43.3854V11.0617C43.3854 9.31973 42.2154 8.39673 40.4994 8.39673ZM40.2004 13.9477C39.6024 13.9477 39.2384 13.6097 39.2384 13.1547C39.2384 12.6867 39.6804 12.3227 40.5514 12.2187L41.7994 12.0757V12.2187C41.7994 13.1807 41.2144 13.9477 40.2004 13.9477ZM47.5592 15.1567C48.3782 15.1567 49.1062 14.8187 49.4312 14.2987V15.0527H51.0692V5.84873H49.4312V9.22873C49.0542 8.73473 48.3522 8.39673 47.5332 8.39673C45.5962 8.39673 44.4392 9.77473 44.4392 11.7767C44.4392 13.7787 45.5962 15.1567 47.5592 15.1567ZM47.7932 13.7397C46.7532 13.7397 46.0642 12.9597 46.0642 11.7767C46.0642 10.5937 46.7532 9.81373 47.7932 9.81373C48.8462 9.81373 49.5352 10.5937 49.5352 11.7767C49.5352 12.9597 48.8462 13.7397 47.7932 13.7397ZM58.6413 11.5557C58.6413 10.0347 57.5623 8.39673 55.3523 8.39673C53.1813 8.39673 52.1153 10.0737 52.1153 11.7767C52.1153 13.4797 53.2723 15.1567 55.4433 15.1567C56.9773 15.1567 58.1213 14.4287 58.4853 13.1937L56.9513 12.7777C56.7823 13.4147 56.2363 13.7787 55.4693 13.7787C54.5333 13.7787 53.8963 13.1937 53.7533 12.2057H58.6153C58.6283 12.0757 58.6413 11.7897 58.6413 11.5557ZM53.7923 11.0097C53.9743 10.1257 54.5463 9.64473 55.3523 9.64473C56.3013 9.64473 56.8083 10.2297 56.8863 11.0097H53.7923ZM64.1474 15.0527H65.6034L66.9554 10.7757L68.2814 15.0527H69.7504L71.7784 8.50073H70.1274L69.0094 12.7647L67.6964 8.50073H66.2014L64.8884 12.7647L63.7574 8.50073H62.1064L64.1474 15.0527ZM72.6115 5.84873V7.53873H74.3795V5.84873H72.6115ZM72.6635 15.0527H74.3015V8.50073H72.6635V15.0527ZM79.5801 14.9877L79.4631 13.5577C79.2291 13.6747 78.9431 13.7137 78.6961 13.7137C78.1891 13.7137 77.8511 13.3757 77.8511 12.7517V9.87873H79.5021V8.50073H77.8511V6.57673H76.2131V8.50073H75.1081V9.87873H76.2131V12.9077C76.2131 14.4417 77.2011 15.1567 78.5271 15.1567C78.9041 15.1567 79.2551 15.1047 79.5801 14.9877ZM80.8429 15.0527H82.4809V12.2057C82.4809 10.7237 83.0529 9.85273 84.0669 9.85273C84.8729 9.85273 85.3669 10.3597 85.3669 11.1657V15.0527H87.0049V10.7627C87.0049 9.33273 86.0299 8.39673 84.5609 8.39673C83.6509 8.39673 82.9099 8.79973 82.4809 9.51473V5.84873H80.8429V15.0527ZM95.4411 15.1567C96.5461 15.1567 97.5471 14.6887 97.9501 13.9477V15.0527H99.5491V10.1907H95.2071V11.6207H97.9111C97.8201 12.8037 96.8971 13.6227 95.5191 13.6227C93.9201 13.6227 92.7631 12.4917 92.7631 10.5027C92.7631 8.51373 93.9201 7.36973 95.5451 7.36973C96.7671 7.36973 97.6771 8.09773 97.8981 9.24173L99.6401 8.87773C99.2891 6.99273 97.7421 5.84873 95.5581 5.84873C93.1791 5.84873 90.9821 7.39573 90.9821 10.5027C90.9821 13.6097 93.1271 15.1567 95.4411 15.1567ZM100.9 15.0527H102.538V5.84873H100.9V15.0527ZM103.878 5.84873V7.53873H105.646V5.84873H103.878ZM103.93 15.0527H105.568V8.50073H103.93V15.0527ZM109.729 15.1567C110.548 15.1567 111.276 14.8187 111.601 14.2987V15.0527H113.239V5.84873H111.601V9.22873C111.224 8.73473 110.522 8.39673 109.703 8.39673C107.766 8.39673 106.609 9.77473 106.609 11.7767C106.609 13.7787 107.766 15.1567 109.729 15.1567ZM109.963 13.7397C108.923 13.7397 108.234 12.9597 108.234 11.7767C108.234 10.5937 108.923 9.81373 109.963 9.81373C111.016 9.81373 111.705 10.5937 111.705 11.7767C111.705 12.9597 111.016 13.7397 109.963 13.7397ZM120.811 11.5557C120.811 10.0347 119.732 8.39673 117.522 8.39673C115.351 8.39673 114.285 10.0737 114.285 11.7767C114.285 13.4797 115.442 15.1567 117.613 15.1567C119.147 15.1567 120.291 14.4287 120.655 13.1937L119.121 12.7777C118.952 13.4147 118.406 13.7787 117.639 13.7787C116.703 13.7787 116.066 13.1937 115.923 12.2057H120.785C120.798 12.0757 120.811 11.7897 120.811 11.5557ZM115.962 11.0097C116.144 10.1257 116.716 9.64473 117.522 9.64473C118.471 9.64473 118.978 10.2297 119.056 11.0097H115.962Z"
            fill="currentColor"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM12.9808 13.0357C12.5964 14.2651 11.6597 15.088 10.1731 15.3591C8.06951 15.7428 6.07227 14.8029 5.51212 11.7316C5.00568 8.95482 5.82995 5.37021 9.77073 4.65148C12.0286 4.23968 13.6611 5.08669 14.25 6.96452L12.4831 7.60556C12.0935 6.34347 11.1816 5.8722 9.91943 6.1024C7.42314 6.55769 7.0383 9.29415 7.41684 11.3697C7.80818 13.5154 8.85531 14.0924 10.2858 13.8315C12.3053 13.4632 12.7608 11.8296 12.7577 10.8593L12.7576 10.5406L10.0649 11.0317L10.0642 9.59723L14.3976 8.80688L14.421 14.338L13.1308 14.5733L12.9808 13.0357Z"
            fill="currentColor"
        />
    </svg>
);

class Flyout extends SubscribingReactComponent<Props, State> {
    public state: State = {};

    protected expandState(): State {
        return {};
    }

    private onFlyoutMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    private onSignInClick = async (_ev: React.MouseEvent<HTMLImageElement>): Promise<void> => {
        this.props.onClose();
        await this.props.modals.openAuthModal(getLocalizedString("signIn"), AuthScreenMode.Username, true);
    };

    private onShare = async () => {
        this.props.onClose();
        pushShareScreen(this.props.renderEnvironment);
    };

    private onLogOut = async () => {
        this.props.appEnvironment.signOut();
    };

    private shouldExpandLeftPanel = (isTablet: boolean, width: number): boolean => {
        return isTablet && width > leftPanelExpandBreakpoint;
    };

    public recompute(force: ForceValueProducer): React.ReactNode {
        const {
            isOpen,
            onClose,
            items,
            onSelected,
            credentials,
            appEnvironment,
            userProfile,
            renderEnvironment,
        } = this.props;

        const { isBuilder, eminenceFlags, enableShareScreen, shareScreenTitle } = appEnvironment;
        const target = document.getElementById(FLYOUT_ROOT);

        if (target === null) return null;

        let name = getLocalizedString("signIn");
        let email = "";
        let imageURL: string | undefined;

        if (isBuilder || credentials.isAuthenticated) {
            if (userProfile !== undefined) {
                const inspector = userProfile.getObjectInspector(force);
                if (isLoadedDefined(inspector)) {
                    const maybeEmail = appEnvironment.authenticator.realEmail;
                    const maybeName = loadedDefinedMap(definedMap(inspector.getProperty("name"), force), asString);
                    const maybeImage = loadedDefinedMap(definedMap(inspector.getProperty("image"), force), asString);
                    if (undefinedIfEmptyOrWhitespaceString(maybeEmail) !== undefined) {
                        email = defined(maybeEmail);
                        if (undefinedIfEmptyOrWhitespaceString(maybeName) !== undefined) {
                            name = defined(maybeName);
                        } else {
                            name = "";
                        }
                    }
                    if (undefinedIfEmptyOrWhitespaceString(maybeImage) !== undefined) {
                        imageURL = defined(maybeImage);
                    }
                }
            } else if (credentials.isAuthenticated) {
                name = credentials.username;
                email = credentials.email;
            }
        }

        const headerImg = <HeaderImg src={imageURL} alternate={<NoUserImg />} />;

        const content = (
            <FlyoutContainer
                className={isOpen ? "open" : "closed"}
                onClick={onClose}
                aria-hidden={!isOpen}
                aria-label="Flyout menu">
                <Swipe onSwipeLeft={onClose} tolerance={100}>
                    <ReactResizeDetector handleWidth={true} resizableElementId="root">
                        {(width: number, _height: number) => {
                            const expandLeftPanel = this.shouldExpandLeftPanel(
                                renderEnvironment.sizeClass !== DeviceFormFactor.Phone,
                                width
                            );

                            const shareScreenTitleOrDefault =
                                shareScreenTitle !== "" ? shareScreenTitle : getLocalizedString("about");

                            const authed: boolean = isBuilder || credentials.isAuthenticated;
                            return (
                                <FlyoutMenuWrapper
                                    onClick={this.onFlyoutMenuClick}
                                    expandLeftPanel={expandLeftPanel}
                                    role="aside">
                                    <Header
                                        onClick={
                                            authed
                                                ? () => {
                                                      this.props.onClose();
                                                      appEnvironment.pushUserProfileScreen();
                                                  }
                                                : this.onSignInClick
                                        }
                                        role="button"
                                        tabIndex={0}
                                        aria-label={authed ? `User profile` : `Sign in`}>
                                        {headerImg}
                                        <HeaderName>{name}</HeaderName>
                                        {email !== "" && <HeaderEmail>{email}</HeaderEmail>}
                                    </Header>
                                    <Scroller>
                                        <MenuArea role="navigator">
                                            {items.map(i => (
                                                <MenuItemWrapper key={i.desc.title} onClick={() => onSelected(i)}>
                                                    <AppIcon icon={i.desc.icon} size={24} />
                                                    <div>{i.desc.title}</div>
                                                </MenuItemWrapper>
                                            ))}
                                            {enableShareScreen && (
                                                <MenuItemWrapper key="SharePage" onClick={this.onShare}>
                                                    <AppIcon icon={"share"} size={24} />
                                                    <div>{shareScreenTitleOrDefault}</div>
                                                </MenuItemWrapper>
                                            )}
                                            {credentials.isAuthenticated && (
                                                <MenuItemWrapper key="LogOut" onClick={this.onLogOut}>
                                                    <AppIcon icon={"log-out"} size={24} />
                                                    <div>{getLocalizedString("signOut")}</div>
                                                </MenuItemWrapper>
                                            )}
                                        </MenuArea>
                                        {!eminenceFlags.removeBranding && (
                                            <Footer
                                                href={glideViralReferralLink("flyout")}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <MadeWithGlide />
                                            </Footer>
                                        )}
                                    </Scroller>
                                </FlyoutMenuWrapper>
                            );
                        }}
                    </ReactResizeDetector>
                </Swipe>
            </FlyoutContainer>
        );
        return createPortal(content, target);
    }
}

export default withModals(withCredentials(Flyout));
